import {
  OmniElement,
  OmniStyleElement,
  html,
  css,
  OmniIconElement,
  classMap,
} from 'omni-ui';
import { Om2Dropdown } from 'omni-campaign-ui';
import CampaignsAPI from '../services/campaigns-api.js';

OmniStyleElement.register();
OmniIconElement.register();
Om2Dropdown.register();

/*
 * Attempt to encapsulate all logic that deals with step status. Specifically:
 *  1. Render dropdown to change the step status
 *  2. Render a readonly version of the current status with an icon
 *  3. Calculate campaign % complete based on step status
 *
 * For points 1 and 2 to work in different locations on the page, and without
 * requiring re-pulling the campaign from the backend, when changing the status the
 * active campaign object is updated. So long as other CampaignStepStatus instances
 * refer to the same campaign object they will reflect the correct updated status on
 * their next re-render. An alternative would be to send/listen to events instead.
 */
export class CampaignStepStatus extends OmniElement {
  constructor() {
    super();
    // The available status dropdown options.
    this.defaultOptions = [
      {
        uuid_framework_template_status: 'not_started',
        icon: 'omni:informative:notStarted',
        name: 'Not Started',
        completion: 0,
      },
      {
        uuid_framework_template_status: 'in_progress',
        icon: 'omni:informative:inProgress',
        name: 'In Progress',
        completion: 0,
      },
      {
        uuid_framework_template_status: 'in_review',
        icon: 'omni:informative:inReview',
        name: 'In Review',
        completion: 0,
      },
      {
        uuid_framework_template_status: 'skipped',
        icon: 'omni:informative:skipped',
        name: 'Skipped',
        completion: 100,
      },
      {
        uuid_framework_template_status: 'complete',
        icon: 'omni:informative:complete',
        name: 'Complete',
        completion: 100,
      },
    ];
  }

  /*
   * @property {string} stepId - the active step id
   * @property {Object} campaign - the campaign object including all steps and statuses
   * @property {boolean} readonly - Indicates whether to render as readonly
   * @property {string} token - API JWT token
   * @property {string} env - Application environment
   * @property {Object} step - The shared step object (not to be set externally)
   */
  static get properties() {
    return {
      stepId: { type: String },
      campaign: { type: Object },
      readonly: { type: Boolean },
      token: { type: String },
      env: { type: String },
      step: { attribute: false },
      isAltView: { type: Boolean },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        om2-dropdown-impl,
        om2-dropdown-impl {
          background-color: unset !important;
        }
        omni-icon {
          vertical-align: bottom;
        }
        omni-icon[icon-id='omni:informative:complete'],
        omni-icon[icon-id='omni:informative:complete'] + span,
        omni-icon[icon-id='omni:informative:skipped'],
        omni-icon[icon-id='omni:informative:skipped'] + span {
          --color-icon-lines: rgb(var(--rgb-primary));
          color: rgb(var(--rgb-primary));
        }
      `,
    ];
  }

  /*
   * Calculate the percent complete for an entire campaign (or optionally for
   * just a single step). This utility function is used outside of this class.
   *
   * @param {Object} campaign - The campaign object to analyze
   * @param {string} stepId - Optionally filter by a particular step id
   * @return {number} The percent complete calculated
   */
  static percentComplete(campaign, stepId) {
    const count = campaign.framework_template.steps.reduce(
      (sum, step) => sum + (step?.steps?.length ?? 0),
      0,
    );
    let sum = 0;
    Object.entries(campaign.step_status).forEach(([id, status]) => {
      if (stepId && id !== stepId) return;
      sum += status.completion ?? 0;
    });
    return Math.floor(sum / count);
  }

  get options() {
    return this.campaign.framework_template.statuses?.length
      ? this.campaign.framework_template.statuses
      : this.defaultOptions;
  }

  /**
   * (Re)initialize the internal state for a given campaign / stepid
   */
  _init() {
    this.status = this.campaign.step_status[this.stepId];
    // Translate older status format to new format
    if (this.status.id) {
      this.status = {
        ...this.status,
        uuid_framework_template_status: this.status.id,
        name: this.status.label,
      };
      delete this.status.id;
      delete this.status.label;
    }
    // Default to first status if there's no status currently
    if (!this.status?.uuid_framework_template_status) {
      this._updateStatus(this.options[0]);
    }
  }

  /**
   * Send an event to update the campaign step status
   *
   * @param {Object} status - The updated status object
   */
  _updateStatus(status) {
    const detail = { stepId: this.stepId, status };
    this.dispatchNewEvent('step-status-change', { detail });
  }

  shouldUpdate(changedProperties) {
    if (
      (changedProperties.has('stepId') && this.campaign) ||
      (changedProperties.has('campaign') && this.stepId)
    ) {
      this._init();
    }
    if (this.token && this.env && !this.campaignsAPI) {
      this.campaignsAPI = new CampaignsAPI(this.token, this.env);
    }
    return true;
  }

  render() {
    return html` <omni-style>
      ${this.readonly
        ? html`
            <div>
              <omni-icon
                class="is-size-1 is-inline-block mr-2"
                icon-id="${this.status.icon}"
                aria-hidden="true"
              ></omni-icon>
              <span
                class="${classMap({
                  'is-size-6': true,
                  'is-hidden': this.isAltView,
                })}"
                >${this.status.name}</span
              >
            </div>
          `
        : html`
            <om2-dropdown
              hasObjectValues
              borderless
              idProperty="uuid_framework_template_status"
              nameProperty="name"
              @change=${e => {
                const status = e.detail.value;
                const dropdown = e.target;
                if (
                  status.uuid_framework_template_status ===
                  this.status.uuid_framework_template_status
                )
                  return;
                dropdown.value = { ...this.status }; // Revert until validation

                // Update step status
                this._updateStatus(status);
                dropdown.value = status; // Set dropdown again

                // Update step status in backend
                this.campaignsAPI.updateStep(
                  this.campaign.uuid_campaign,
                  this.stepId,
                  { status },
                );
              }}
              .options=${this.options}
              .value=${this.status}
            ></om2-dropdown>
          `}
    </omni-style>`;
  }
}

OmniElement.register('campaign-step-status', CampaignStepStatus);
